import { z } from 'zod';

export const RoleSchema = z.object({
    id: z.number(),
    name: z.string(),
    level: z.number(),
    deletable: z.boolean().nullish(),
    // grants comes back as an array of numeric codes; we don't render it but
    // keep it permissive so a shape change doesn't break the page.
    grants: z.array(z.number()).nullish(),
});

export type Role = z.infer<typeof RoleSchema>;

// Lightweight row returned by GET /users (list). Discord + role come along,
// addresses / orders do not (those are detail-only).
export const UserListItemSchema = z.object({
    id: z.number(),
    name: z.string().nullish(),
    email: z.string(),
    active: z.boolean(),
    newsletters: z.boolean().nullish(),
    phone: z.string().nullish(),
    image: z.string().nullish(),
    discordId: z.string().nullish(),
    discordUsername: z.string().nullish(),
    discordAvatar: z.string().nullish(),
    discordVerifiedAt: z.string().nullish(),
    roleId: z.number(),
    role: RoleSchema.nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
});

export type UserListItem = z.infer<typeof UserListItemSchema>;

// Order summary as returned by the user detail include (limited attributes).
export const UserOrderSchema = z
    .object({
        id: z.number(),
        orderNumber: z.string().nullish(),
        status: z.string().nullish(),
        paymentStatus: z.string().nullish(),
        grandTotal: z.union([z.number(), z.string()]).nullish(),
        createdAt: z.string().nullish(),
        isGuestOrder: z.boolean().optional(),
    })
    .passthrough();

export type UserOrder = z.infer<typeof UserOrderSchema>;

// Addresses are returned without the city/county lookups joined, so we only
// assert what we render and pass the rest through untouched.
export const UserAddressSchema = z
    .object({
        id: z.number(),
        addressName: z.string().nullish(),
        name: z.string().nullish(),
        email: z.string().nullish(),
        phoneNumber: z.string().nullish(),
        address: z.string().nullish(),
        isCompany: z.boolean().nullish(),
        companyName: z.string().nullish(),
        vatNumber: z.string().nullish(),
        registryNumber: z.string().nullish(),
        other: z.string().nullish(),
        active: z.boolean().nullish(),
    })
    .passthrough();

export type UserAddress = z.infer<typeof UserAddressSchema>;

export const UserDetailSchema = UserListItemSchema.extend({
    lastDiscordSyncAt: z.string().nullish(),
    impersonatingUserId: z.number().nullish(),
    addresses: z.array(UserAddressSchema).nullish(),
    orders: z.array(UserOrderSchema).nullish(),
});

export type UserDetail = z.infer<typeof UserDetailSchema>;

export const UserListResponseSchema = z.object({
    success: z.boolean(),
    data: z.array(UserListItemSchema),
    count: z.number(),
});

export const UserDetailResponseSchema = z.object({
    success: z.boolean(),
    data: UserDetailSchema,
});

export const RoleListResponseSchema = z.object({
    success: z.boolean(),
    data: z.array(RoleSchema),
    count: z.number().optional(),
});

// Form schema. Password is optional on edit ("leave blank to keep current");
// the create page enforces presence via superRefine when no user id is set.
// passwordConfirmation must match when a password is supplied.
export const UserFormSchema = z
    .object({
        name: z.string().max(255, 'Numele are maxim 255 de caractere').optional().or(z.literal('')),
        email: z
            .string()
            .min(1, 'Emailul este obligatoriu')
            .email('Adresa de email nu este valida'),
        phone: z.string().max(50, 'Telefonul are maxim 50 de caractere').optional().or(z.literal('')),
        roleId: z.coerce.number().int().positive('Rolul este obligatoriu'),
        password: z
            .string()
            .min(8, 'Parola trebuie sa aiba minim 8 caractere')
            .max(255, 'Parola are maxim 255 de caractere')
            .optional()
            .or(z.literal('')),
        passwordConfirmation: z.string().optional().or(z.literal('')),
        active: z.boolean(),
        newsletters: z.boolean(),
    })
    .superRefine((values, ctx) => {
        if (values.password && values.password !== values.passwordConfirmation) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['passwordConfirmation'],
                message: 'Parolele nu coincid',
            });
        }
    });

export type UserForm = z.infer<typeof UserFormSchema>;

// Address edit form. Mirrors the server-side Address validators: email/phone/
// address are required, and company addresses must carry their fiscal fields.
// cityId/countyId are intentionally omitted — they're left untouched on save.
export const AddressFormSchema = z
    .object({
        addressName: z.string().optional().or(z.literal('')),
        name: z.string().optional().or(z.literal('')),
        email: z.string().min(1, 'Emailul este obligatoriu').email('Email invalid'),
        phoneNumber: z.string().min(1, 'Telefonul este obligatoriu'),
        address: z.string().min(1, 'Adresa este obligatorie'),
        isCompany: z.boolean(),
        companyName: z.string().optional().or(z.literal('')),
        vatNumber: z.string().optional().or(z.literal('')),
        registryNumber: z.string().optional().or(z.literal('')),
        other: z.string().optional().or(z.literal('')),
    })
    .superRefine((values, ctx) => {
        if (!values.isCompany) return;
        for (const field of ['companyName', 'vatNumber', 'registryNumber'] as const) {
            if (!values[field]) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [field],
                    message: 'Obligatoriu pentru firme',
                });
            }
        }
    });

export type AddressForm = z.infer<typeof AddressFormSchema>;

// Player identifiers — per-user TCG player IDs (DCI / Konami ID style).
// Keep PLAYER_GAMES in lockstep with the API ENUM
// (api/modules/player-identifiers/models/player-identifier.model.js).
export const PLAYER_GAMES = ['mtg', 'ygo'] as const;

export const PlayerGameSchema = z.enum(PLAYER_GAMES);

export type PlayerGame = z.infer<typeof PlayerGameSchema>;

export const PLAYER_GAME_LABELS: Record<PlayerGame, string> = {
    mtg: 'Magic: The Gathering',
    ygo: 'Yu-Gi-Oh!',
};

export const PlayerIdentifierSchema = z.object({
    id: z.number(),
    userId: z.number(),
    game: PlayerGameSchema,
    identifier: z.string(),
    customData: z.record(z.unknown()).nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
});

export type PlayerIdentifier = z.infer<typeof PlayerIdentifierSchema>;

export const PlayerIdentifierListResponseSchema = z.object({
    success: z.boolean(),
    data: z.array(PlayerIdentifierSchema),
    count: z.number().optional(),
});

export const PlayerIdentifierResponseSchema = z.object({
    success: z.boolean(),
    data: PlayerIdentifierSchema,
});

// customData is edited as free JSON text; empty clears it, otherwise it must
// parse to a plain object (the API rejects arrays/scalars too).
export const PlayerIdentifierFormSchema = z.object({
    game: PlayerGameSchema,
    identifier: z
        .string()
        .min(1, 'Identificatorul este obligatoriu')
        .max(255, 'Identificatorul are maxim 255 de caractere'),
    customData: z
        .string()
        .optional()
        .or(z.literal(''))
        .superRefine((value, ctx) => {
            if (!value || !value.trim()) return;
            try {
                const parsed = JSON.parse(value);
                if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
                    throw new Error('not an object');
                }
            } catch {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Trebuie sa fie un obiect JSON valid, ex. {"arena": "Player#123"}',
                });
            }
        }),
});

export type PlayerIdentifierForm = z.infer<typeof PlayerIdentifierFormSchema>;

// Romanian display labels per currency code. 'points' is all we have today.
export const CURRENCY_LABELS: Record<string, string> = {
    points: 'KOINs',
};

export const UserCurrencyBalanceSchema = z.object({
    id: z.number().nullable(),
    userId: z.number(),
    currency: z.string(),
    balance: z.number(),
});

export type UserCurrencyBalance = z.infer<typeof UserCurrencyBalanceSchema>;

export const UserCurrencyBalanceListResponseSchema = z.object({
    success: z.boolean(),
    data: z.array(UserCurrencyBalanceSchema),
    count: z.number().optional(),
});

export const UserCurrencyLogSchema = z.object({
    id: z.number(),
    userId: z.number(),
    currency: z.string(),
    amount: z.number(),
    balanceAfter: z.number(),
    reason: z.string().nullish(),
    note: z.string().nullish(),
    source: z.string(),
    actorUserId: z.number().nullish(),
    actor: z
        .object({ id: z.number(), name: z.string().nullish(), email: z.string() })
        .nullish(),
    createdAt: z.string().nullish(),
});

export type UserCurrencyLog = z.infer<typeof UserCurrencyLogSchema>;

export const UserCurrencyLogListResponseSchema = z.object({
    success: z.boolean(),
    data: z.array(UserCurrencyLogSchema),
    count: z.number(),
});

// Adjust form: a positive amount plus a direction (the panel converts these
// into the signed delta the API expects) and an optional note.
export const CurrencyAdjustFormSchema = z.object({
    direction: z.enum(['credit', 'debit']),
    amount: z.coerce
        .number({ invalid_type_error: 'Introdu o suma' })
        .positive('Suma trebuie sa fie mai mare ca zero'),
    note: z.string().optional().or(z.literal('')),
});

export type CurrencyAdjustForm = z.infer<typeof CurrencyAdjustFormSchema>;
